import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import decode from 'jwt-decode';
import { StorageService } from '../utils/storage.service';
import { Auth, IAccessTokenDetails } from 'src/app/core/domain';
import { ToastAlertService } from '../utils/toast.service';
import { ACCOUNT_TYPE_TO_DASHBOARD_UTL } from '../constants/navlinks';
import { AuthService } from 'src/app/core/service';
import * as ProfileActions from 'src/app/core/state/profile/profile.action';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private storageService: StorageService,
    private toastService: ToastAlertService,
    private router: Router,
    private authService: AuthService,
    private store$: Store<any>
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    let expectedType = route.data['expectedType'] as Array<string>;
    const user: Auth = this.storageService.getObject(StorageService.storageKeys.user);
    if (user) {
      return this.checkAccountType(user, expectedType);
    }

    /* SAN-384: a hub->spoke cross-subdomain redirect right after login lands
       here with an empty localStorage (it never crosses subdomains), even
       though the httpOnly accessToken cookie is still valid for this
       tenant's shared apiUrl host. Confirm via the API before logging the
       user out, instead of trusting bare localStorage absence. */
    return this.authService.validateSession().pipe(
      map((sessionUser: Auth) => {
        if (!sessionUser) {
          return this.showErrorAndLogout(state);
        }
        this.storageService.setObject(StorageService.storageKeys.user, sessionUser);
        /* SAN-535: the normal login$ effect (auth.effect.ts) dispatches this
           right after storing 'user' -- PublicLayoutSidebarComponent and
           DashboardV2Component both key their nav menu / widgets off the
           resulting profileData store slice, not off the trimmed Auth
           object above. Without it, getNavMenusByUserType() sees an
           undefined accountType and falls back to the anonymous nav,
           which is exactly the "sidebar/widgets missing" symptom this
           fallback path caused before this fix. */
        this.store$.dispatch(new ProfileActions.GetProfile());
        return this.checkAccountType(sessionUser, expectedType);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 401) {
          // The 401 interceptor already cleared storage, toasted, logged
          // out and navigated to /auth/login -- just preserve the intended
          // destination without a second logout/toast.
          localStorage.setItem('urlBeforeLoginIn', state.url);
          return of(false);
        }
        return of(this.showErrorAndLogout(state));
      })
    );
  }

  private checkAccountType(user: Auth, expectedType?: Array<string>): boolean | UrlTree {
    if (expectedType && expectedType.length && !expectedType.includes(user.accountType)) {
      const redirectUrl = ACCOUNT_TYPE_TO_DASHBOARD_UTL[user.accountType];
      this.router.navigate([redirectUrl])
      return false;
    }

    return true;
  }

  showErrorAndLogout(state: RouterStateSnapshot, msg = 'Error logging in',) {
    this.toastService.showToast(msg, 'error');
    console.error('Error while decoding access token');
    this.authService.logout();

    localStorage.setItem('urlBeforeLoginIn', state.url);

    return false;
  }

}
